import { R } from 'config/app';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import redirectToDashboard from '../../api/utils/redirectToDashboard';
import useClientQuery from './useClientQuery';
import useLoanApplicationQuery from './useLoanApplicationQuery';
import useValidAMLExistsQuery from './useValidAMLExistsQuery';

const useHandleExistingLoginNavigation = () => {
  const [enabled, setEnabled] = useState(false);
  const { data: clientData, isSuccess: clientIsSuccess } =
    useClientQuery(enabled);
  const { loanApplication, status: loanApplicationStatus } =
    useLoanApplicationQuery(enabled);
  const { data: validAmlExistsData, isSuccess: validAmlIsSuccess } =
    useValidAMLExistsQuery(enabled);

  const navigate = useNavigate();

  const handleLoginNavigation = useCallback(() => {
    setEnabled(true);
  }, []);

  useEffect(() => {
    if (!clientIsSuccess) {
      return;
    }

    if (clientData?.status === 'IDENTIFIED') {
      redirectToDashboard();
      return;
    }

    if (clientData?.blocked) {
      navigate(R.FirstLoanRejected);
      return;
    }

    if (!validAmlIsSuccess) {
      return;
    }

    if (!validAmlExistsData?.validAmlExists || !loanApplication?.confirmed) {
      navigate(R.FirstLoanQuestionnaire);
      return;
    }

    if (loanApplicationStatus !== 'success') {
      return;
    }

    if (!loanApplication?.status) {
      navigate(R.FirstLoanWelcomeBack);
      return;
    }

    if (loanApplication.status === 'OPEN') {
      if (clientData.markedForManualIdentification) {
        navigate(R.FirstLoanPendingManual);
        return;
      }

      navigate(R.FirstLoanIdentificationMethod);
      return;
    }

    if (loanApplication.status === 'CLOSED') {
      if (loanApplication.resolutionMessage === 'Fast delivery fee paid') {
        navigate(R.FirstLoanFdfApplied);
        return;
      }

      if (
        loanApplication.resolution === 'APPROVED' ||
        (loanApplication.resolution === 'MANUAL' &&
          loanApplication.resolutionDetail === 'PENDING_FAST_DELIVERY_FEE')
      ) {
        navigate(R.FirstLoanApprovedManual);
        return;
      }

      if (
        loanApplication.resolution === 'REJECTED' ||
        loanApplication.resolution === 'CANCELLED'
      ) {
        navigate(R.FirstLoanWelcomeBack, { state: 'new' });
        return;
      }
    }

    // Default behavior
    navigate(R.FirstLoanQuestionnaire);
  }, [
    clientData?.blocked,
    clientIsSuccess,
    loanApplicationStatus,
    loanApplication?.status,
    validAmlIsSuccess,
    validAmlExistsData?.validAmlExists,
    loanApplication?.resolution,
    loanApplication?.resolutionDetailMessage,
    loanApplication?.resolutionDetail,
    navigate,
  ]);

  return {
    handleLoginNavigation,
  };
};

export default useHandleExistingLoginNavigation;
